import { useEffect, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core';
import home from './../../assets/icons/home.svg';
import office from './../../assets/icons/office.svg';
import officeSTO from './../../assets/icons/office-sto.svg';
import officeHEL from './../../assets/icons/office-hel.svg';
import absent from './../../assets/icons/absent.svg';
import elsewhere from './../../assets/icons/elsewhere.svg';
import court from './../../assets/icons/court.svg';
import none from './../../assets/icons/none.svg';
import classNames from 'classnames';
import moment from 'moment';
import { UserDto } from 'utils/wemble-api.generated';
import { useCreateWorkplaceForecastMutation } from 'utils/wemble-api';
import organization from 'utils/organization';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.gray.g7,
    maxHeight: 220,
    position: 'relative',
    height: '100%',
    borderRadius: 24,
    transition: '0.2s',
    overflow: 'visible',

  },
  firstFlexContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '12px 16px',
    [theme.breakpoints.down('xs')]: {
      padding: '12px 14px',
    },
    pointerEvents: 'auto',
    transition: 'margin-left 0.5s'





  },
  divider: {
    width: '34px',
    height: '100%',
    position: 'relative',
    cursor: 'pointer',
    pointerEvents: 'auto',
    transition: 'margin-left 0.5s',

  },
  divider__icon: {
    marginLeft: '8px',
    marginTop: '25px',
    transition: 'transform 0.25s'

  },
  secondFlexContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: 'calc(100% - 24px)',
    padding: '12px 16px',
    [theme.breakpoints.down('sm')]: {
      padding: '6px 0',
    },
    pointerEvents: 'auto',
    transition: 'margin-left 0.5s'

  },
  firstContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    borderRadius: 24,
    overflow: 'hidden',
    pointerEvents: 'auto'



  },
  dividerContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    borderRadius: 24,
    overflow: 'hidden',
    pointerEvents: 'auto'



  },

  secondContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    borderRadius: 24,
    overflow: 'hidden',
    pointerEvents: 'none'





  },
  container__item: {
    position: 'relative',
  },
  item: {
    width: 56,
    height: 56,
    [theme.breakpoints.down('md')]: {
      width: 42,
      minHeight: 42,
      height: 46,
    },
    overflow: 'hidden',
    borderRadius: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transition: '0.1s',
    position: 'relative',
    zIndex: 10,
  },
  item__option: {
    backgroundColor: theme.palette.blueGray.bg1,
    borderRadius: '50%',
    margin: '6px 0',
    width: 44,
    minHeight: 44,
    height: 44,
    [theme.breakpoints.down('md')]: {
      width: 40,
      minHeight: 40,
      height: 40,
    },

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: '0.1s',
    '& img': {
      width: '60%',
      filter: 'brightness(0) invert(1)',
      transition: '0.1s',
    },
    '&:hover': {
      opacity: 0.7,
    },
  },
  item__optionDisabled: {
    '&:hover': {
      backgroundColor: `${theme.palette.state.disabled} !important`,
    },
  },
  item__day: {
    position: 'absolute',
    top: 52,
    width: '100%',
    textAlign: 'center',
    fontWeight: 600,

  },
  item__title: {
    position: 'absolute',
    top: -15,
    left: 6,
    width: '300%',
    fontWeight: 600,
    fontSize: 11,
  },
  item__tooltip: {
    position: 'absolute',
    bottom: -28,
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: 10,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    color: '#333',
    backgroundColor: 'white',
    padding: '3px 8px',
    borderRadius: 6,
    boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
    pointerEvents: 'none',
    zIndex: 20,
  },
}));

type StatusType = { value: number | null; type: 'home' | 'office' | 'office-sto' | 'office-hel' | 'absent' | 'elsewhere' | 'none'; img: string; label: string };

const statusTypes: StatusType[] = [
  { value: 0, type: 'office', img: office, label: 'Office' },
  { value: 1, type: 'home', img: home, label: 'Home' },
  { value: 3, type: 'elsewhere', img: elsewhere, label: 'Elsewhere' },
  { value: 2, type: 'absent', img: absent, label: 'Away' },
  { value: null, type: 'none', img: none, label: 'Clear' },

];

function statusBgColor(value: number | null, defaultColor: string, disabledColor: string): string {
  switch (value) {
    case 0: return defaultColor; // office — neutral
    case 1: return '#4A90D9'; // home — light blue
    case 2: return disabledColor; // absent
    case 3: return '#5BAD7A'; // elsewhere — green
    default: return defaultColor;
  }
}

// TODO: Dead code — dual-office (Stockholm/Helsinki) feature is no longer used by Hannes Snellman. Can be removed.
const statusTypesHS: StatusType[] = [
  { value: 0, type: 'office-sto', img: officeSTO, label: 'Stockholm' },
  { value: 3, type: 'office-hel', img: officeHEL, label: 'Helsinki' },
  { value: 1, type: 'home', img: home, label: 'Home' },
  { value: 2, type: 'absent', img: absent, label: 'Away' },

];
function returnDayOfWeek(dayNumber: number, currentDay: number, future: boolean) {
  if (!future && dayNumber === currentDay) {
    return 'TODAY';
  }
  switch (dayNumber) {
    case 0:
      return 'MON';
    case 1:
      return 'TUE';
    case 2:
      return 'WED';
    case 3:
      return 'THU';
    case 4:
      return 'FRI';
    default:
      return '';
  }
}
const DayItem = ({
  index,
  day,
  currentDay,
  value,
  disabled,
  enableElsewhere = false,
  dualOffices = false,
  future = false,
  enableInCourt = false,
  fetchUsers
}: {
  index: number;
  day: number;
  currentDay: number;
  value: number | null;
  disabled: boolean;
  enableElsewhere: boolean;
  dualOffices: boolean;
  future?: boolean;
  enableInCourt?: boolean;
  fetchUsers: () => Promise<void>
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [createWorkplaceForecast] = useCreateWorkplaceForecastMutation();
  const [hovered, setHovered] = useState(false);
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
  const [activeOption, setActiveOption] = useState<StatusType>();
  const [options, setOptions] = useState<StatusType[]>([]);

  useEffect(() => {
    setActiveOption((dualOffices ? statusTypesHS : statusTypes).find((obj) => obj.value === value));
    setOptions(dualOffices ? statusTypesHS : statusTypes.filter((obj) => obj.value !== value && (enableElsewhere || obj.type !== 'elsewhere') && (future || obj.type !== 'none')));
  }, [value]);

  function setForecast(value: number | null) {
    createWorkplaceForecast({
      index: index.toString(),
      workplaceForecastCreationParams: {
        option: value
      },
    }).then(_ => fetchUsers());
    setHovered(false);
  }

  return (
    <div
      className={classes.container__item}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ marginTop: future ? 12 : 0 }}
    >

      {future && !index && (<div className={classes.item__title}>
        DEFAULT WEEK
      </div>)}

      <div
        className={classes.item}
        style={hovered ? { height: 56 * (options.length + 1), backgroundColor: '#f2f2f2', borderRadius: '30px', boxShadow: "0px 8px 20px 0px #00000037" } : undefined}
      >


        <div
          className={classNames(classes.item__option, disabled && classes.item__optionDisabled)}
          style={
            disabled || value === null
              ? {
                backgroundColor: theme.palette.state.disabled,
                opacity: 0.7,
              }
              : {
                backgroundColor: statusBgColor(value, theme.palette.blueGray.bg1, theme.palette.state.disabled),
                cursor: 'pointer',
              }
          }
        >
          {value !== null && <img alt={'active-work-location'} src={(activeOption?.type == 'elsewhere' && enableInCourt) ? court : activeOption?.img} />}
        </div>
        {options?.map((option) => (
          <div
            key={'day-option-' + option.value}
            className={classes.item__option}
            onMouseEnter={() => setHoveredLabel(option.label)}
            onMouseLeave={() => setHoveredLabel(null)}
            onClick={(e) => {
              if (disabled) return;
              e.stopPropagation();
              setForecast(option.value);
              setHovered(false);
            }}
            style={{
              backgroundColor: statusBgColor(option.value, theme.palette.blueGray.bg1, theme.palette.state.disabled),
              cursor: 'pointer',
            }}
          >
            <img alt={'option-work-location'} src={(option.type == 'elsewhere' && enableInCourt) ? court : option.img} />
          </div>
        ))}
      </div>
      {hovered && hoveredLabel && (
        <div className={classes.item__tooltip}>{hoveredLabel}</div>
      )}
      <div className={classes.item__day} style={{ opacity: disabled ? 0.5 : 1.0, fontSize: future ? 10 : 14, marginTop: future ? 2 : 6, color: future ? '#000000BB' : 'black' }}>
        {returnDayOfWeek(day, currentDay, future)}
      </div>
      {future}
    </div>

  );
};

const DaySelect = ({ user, disabled = false, fetchUsers }: { user: UserDto | null | undefined; disabled?: boolean, fetchUsers: () => Promise<void> }) => {
  const classes = useStyles();

  const currentDayInWeek = Math.min(moment().isoWeekday() - 1, 5);

  let week = [...Array(5)].map((_, i) => ({
    name: (currentDayInWeek + i) % 5,
    status: (user?.workplaceForecast || [])[i] ?? null
  }));

  return (
    <div className={classes.root}>
      <div className={classes.firstContainer} style={{ overflow: 'visible' }}>
        <div className={classes.firstFlexContainer}>
          {user
            ? week?.map((day, index) => (
              <DayItem
                key={day.name + '-day'}
                index={index}
                day={day.name}
                currentDay={currentDayInWeek}
                value={day.status}
                disabled={disabled}
                fetchUsers={fetchUsers}
                enableInCourt={user.company == "65f779d34b718376e412163f"}
                enableElsewhere={Boolean(organization(user?.company).elseWhereOption)}
                dualOffices={false /* user.company === "5da0c1370b30f993a68d898f" SNELLMAN*/}
              />
            ))
            : null}
        </div>
      </div>
    </div>
  );
};

export default DaySelect;
